import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  ScrollView,
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Platform,
  KeyboardAvoidingView,
  ActivityIndicator,
  Image,
  PermissionsAndroid,
  Alert,
  TouchableWithoutFeedback,
  Keyboard,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import { colorPalette, colors } from '../../../core/config/color.config';
import { width, height, totalSize } from 'react-native-dimension';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { SVGIcon } from '../../components';
import { SafeAreaView } from 'react-native';
import { SendMessageReqBody } from '../../server/types/doctor-admin-chat.types';
import { Controller, useForm } from 'react-hook-form';
import {
  useGetAdminDoctorMessages,
  useSendDoctorAdminMessage,
  useUploadChatAttachment,
} from '../../server/react-query/useAdminChat';
import moment from 'moment';
import { Icon } from 'react-native-elements';
import RNFetchBlob from 'react-native-blob-util';
import DocumentPicker, { types } from 'react-native-document-picker';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import ActionSheet from 'react-native-actionsheet';
// import SendIcon from './../../../assets/images/SendIcon.png';
// import PlusIcon from './../../../assets/images/PlusIcon.png';
import { usePullDownRefreshContext } from '../../stores/pullDownRefreshStore';

interface AdminSearchHeaderProps {
  practitionerId: string;
  senderType: string;
  user: object;
  doctorName?: string;
  practitionerPhotoUrl?: string;
  onDraftTextChange?: (hasDraftText: boolean) => void;
}

const AdminDoctorChat = (props: AdminSearchHeaderProps) => {
  const navigation = useNavigation();

  // Enable LayoutAnimation for Android
  if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
  }

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<SendMessageReqBody>({
    mode: 'all',
    defaultValues: {
      senderType: props.senderType,
      messageText: '',
      practitionerId: '',
    },
    // resolver: yupResolver(schema),
  });

  const MIN_H = 40;
  const MAX_H = 80;
  const PAD_H = 15;
  const PAD_V = 4;
  const INPUT_PAD = 6;

  const watchedMessage = watch('messageText', '');
  const measuredText =
    watchedMessage.length === 0 ? ' ' : watchedMessage + '\n';

  // Calculate font size to match TextInput
  const inputFontSize = Platform.OS === 'web' ? totalSize(1) : totalSize(1.6);
  const inputLineHeight = inputFontSize * 1.25; // Standard line height ratio

  const [conversationId, setConversationId] = useState(null);
  const [practitionerId, setPractitionerId] = useState('');

  // Tell the sidebar whether this (still-conversation-less) compose has
  // unsent text, so it can show a "Draft" entry for a brand-new doctor and
  // remove it again if the draft is cleared without ever being sent.
  useEffect(() => {
    props.onDraftTextChange?.(!conversationId && watchedMessage.trim().length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMessage, conversationId]);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const scrollViewRef = useRef<ScrollView>(null);

  const actionSheetRef = useRef<any>(null);

  const textInputRef = useRef<TextInput>(null);
  const textInputContainerRef = useRef<View>(null);

  const [inputHeight, setInputHeight] = useState(40);
  const [actionsW, setActionsW] = useState(0);
  const [shouldScrollInTextarea, setShouldScrollInTextarea] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const [messages, setMessages] = useState([]);

  const [attachment, setAttachment] = useState({
    image: null,
  });

  const { setCanRefresh } = usePullDownRefreshContext();

  const [isAttachmentUploadLoader, setIsAttachmentUploadLoader] =
    useState(false);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const { data, isLoading } = useGetAdminDoctorMessages(props.practitionerId);

  const sendMessageMutate = useSendDoctorAdminMessage();

  const uploadMutation = useUploadChatAttachment({
    onMutate: () => {
      setIsAttachmentUploadLoader(true);
    },
    onSettled: () => {
      // runs for both success & error
      setTimeout(() => {
        setIsAttachmentUploadLoader(false);
      }, 5000);
    },
  });

  // Tracks whether the admin is currently scrolled to the bottom of the
  // thread, so background polling never yanks the view back down while
  // they're reading older messages (see isAtBottomRef usage below).
  const isAtBottomRef = useRef(true);
  // Signature of the last message list we actually rendered, so a poll
  // that comes back with unchanged data doesn't trigger a pointless
  // re-group/re-render (and the scroll jump that came with it).
  const lastMessagesSignatureRef = useRef<string | null>(null);

  // Switch conversations: reset the compose form and the rendered
  // messages so the previous doctor's thread doesn't flash before the
  // new one loads. Actual fetching/polling is owned by the
  // useGetAdminDoctorMessages query above (keyed by practitionerId).
  useEffect(() => {
    if (props?.practitionerId) {
      // Also clear messageText (not just practitionerId) - otherwise an
      // unsent draft typed for one doctor leaks into the next doctor's
      // compose box when switching away without sending.
      reset(prevValues => ({
        ...prevValues,
        messageText: '',
        practitionerId: props.practitionerId,
      }));

      setPractitionerId(props.practitionerId);
      setMessages([]);
      lastMessagesSignatureRef.current = null;
    }
  }, [props.practitionerId]);

  useEffect(() => {
    if (!data?.messages) return;

    const lastMessage = data.messages[data.messages.length - 1];
    const signature = `${data.messages.length}:${lastMessage?.messageId ?? ''}:${lastMessage?.isRead ?? ''}`;

    if (signature === lastMessagesSignatureRef.current) {
      // Nothing actually changed since the last render - skip re-grouping
      // and re-rendering so an idle admin doesn't see the thread refresh.
      return;
    }

    const isInitialLoadForThisConversation =
      lastMessagesSignatureRef.current === null;
    lastMessagesSignatureRef.current = signature;

    setConversationId(data?.conversationId);

    // Group messages by UTC date (YYYY-MM-DD)
    const grouped = data.messages.reduce((acc: any, msg: any) => {
      const date = moment.utc(msg.createdAt).format('YYYY-MM-DD');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(msg);
      return acc;
    }, {});

    // Sort dates (oldest → newest)
    const sortedDates = Object.keys(grouped).sort(
      (a, b) =>
        moment.utc(a, 'YYYY-MM-DD').valueOf() -
        moment.utc(b, 'YYYY-MM-DD').valueOf(),
    );

    // Ensure messages inside each group are also sorted by time
    const finalData = sortedDates.map(date => ({
      date,
      messages: grouped[date].sort(
        (a: any, b: any) =>
          moment.utc(a.createdAt).valueOf() -
          moment.utc(b.createdAt).valueOf(),
      ),
    }));

    setMessages(finalData);

    // Only auto-scroll on the initial load of this conversation, or when
    // new messages arrive while the admin is already at the bottom.
    // Never yank the view down while they're reading older messages.
    if (isInitialLoadForThisConversation || isAtBottomRef.current) {
      setTimeout(
        () => scrollViewRef.current?.scrollToEnd({ animated: true }),
        isInitialLoadForThisConversation ? 500 : 100,
      );
    }
  }, [data]);

  const scrollToBottom = () => {
    isAtBottomRef.current = true;
    scrollViewRef.current?.scrollToEnd({ animated: true });
  };

  const handleSendPress = () => {
    handleSubmit(sendMessageRequestFunc)();
  };

  const sendMessageRequestFunc = async () => {
    if (getValues().messageText.trim()) {
      const newMsg = {
        conversationId: conversationId || '',
        messageId: Math.random().toString(),
        senderId: props.user.userId,
        senderType: props.senderType,
        messageText: getValues().messageText,
        createdAt: moment.utc().toDate(),
        isRead: false,
      };

      setMessages(prev => {
        const today = moment.utc(newMsg.createdAt).format('YYYY-MM-DD');
        const existingGroup = prev.find(g => g.date === today);

        if (existingGroup) {
          return prev.map(g =>
            g.date === today ? { ...g, messages: [...g.messages, newMsg] } : g,
          );
        } else {
          return [...prev, { date: today, messages: [newMsg] }];
        }
      });
      const formValues = getValues();
      console.log(formValues, 'VAAAA');
      reset();
      await sendMessageMutate.mutateAsync(formValues);
    }
  };

  const handlePickFile = async () => {
    if (Platform.OS === 'web') {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.png,.jpg,.jpeg,.pdf,.doc,.docx';
        input.onchange = (e: any) => {
          const file = e.target.files[0];
          if (file) {
            setAttachment({ image: file });
          }
        };
        input.click();
      } catch (err: any) {
        // On native, check cancel
      }
    } else {
      actionSheetRef.current.show();
    }
  };

  const requestCameraPermission = async () => {
    if (Platform.OS !== 'android') {
      // Web & iOS don’t need this
      return true;
    }
    try {
      const { PermissionsAndroid } = require('react-native');

      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
        {
          title: 'Camera Permission',
          message: 'This app needs access to your camera to take photos.',
          buttonNeutral: 'Ask Me Later',
          buttonNegative: 'Cancel',
          buttonPositive: 'OK',
        },
      );
      return granted === PermissionsAndroid.RESULTS.GRANTED;
    } catch (err) {
      console.warn(err);
      return false;
    }
  };

  const handleOption = async (index: number) => {
    try {
      if (index === 1) {
        const hasPermission =
          Platform.OS === 'android' ? await requestCameraPermission() : true;
        if (!hasPermission) {
          Alert.alert('Camera permission denied');
          return;
        }

        const res = await launchCamera({ mediaType: 'photo' });
        if (!res.didCancel && res.assets?.length > 0) {
          const asset = res.assets[0];
          setAttachment({
            image: {
              uri: asset.uri,
              name: asset.fileName || `photo_${Date.now()}.jpg`,
              type: asset.type || 'image/jpeg',
            },
          });
        }
      } else if (index === 2) {
        const res = await launchImageLibrary({ mediaType: 'photo' });
        if (!res.didCancel && res.assets?.length > 0) {
          const asset = res.assets[0];
          setAttachment({
            image: {
              uri: asset.uri,
              name: asset.fileName || `image_${Date.now()}.jpg`,
              type: asset.type || 'image/jpeg',
            },
          });
        }
      } else if (index === 3) {
        const file = await DocumentPicker.pickSingle({
          type: [types.pdf, types.doc, types.docx],
        });
        setAttachment({
          image: {
            uri: file.uri,
            name: file.name,
            type: file.type,
          },
        });
      }
    } catch (err) {
      if (!DocumentPicker.isCancel(err)) {
        console.error('Attachment pick error:', err);
      }
    }
  };

  // const downloadFile = async (url: string, fileName: string) => {
  //   if (Platform.OS === 'web') {
  //     try {
  //       const response = await fetch(url);
  //       const blob = await response.blob();

  //       const link = document.createElement('a');
  //       link.href = window.URL.createObjectURL(blob);
  //       link.download = fileName || 'download';
  //       document.body.appendChild(link);
  //       link.click();

  //       document.body.removeChild(link);
  //       window.URL.revokeObjectURL(link.href); // cleanup
  //     } catch (err) {
  //       console.error('Download failed', err);
  //     }
  //   } else {
  //     RNFetchBlob.config({
  //       addAndroidDownloads: {
  //         useDownloadManager: true,
  //         notification: true,
  //         path: RNFetchBlob.fs.dirs.DownloadDir + '/' + fileName,
  //         description: 'Downloading file...',
  //       },
  //     })
  //       .fetch('GET', url)
  //       .then(res => {
  //         console.log('File saved to:', res.path());
  //       })
  //       .catch(err => {
  //         console.error('Download failed', err);
  //       });
  //   }
  // };

  const downloadFile = async (url: string, fileName: string) => {
    if (Platform.OS === 'ios') {
      const { dirs } = RNFetchBlob.fs;
      const path = `${dirs.DocumentDir}/${fileName}`;

      try {
        const res = await RNFetchBlob.config({
          fileCache: true,
          path,
        }).fetch('GET', url);

        // console.log('File saved to:', res.path());

        // Open iOS share dialog so user can save/export
        RNFetchBlob.ios.openDocument(res.path());
      } catch (err) {
        console.error('Download failed', err);
      }
    } else if (Platform.OS === 'android') {
      RNFetchBlob.config({
        addAndroidDownloads: {
          useDownloadManager: true,
          notification: true,
          path: RNFetchBlob.fs.dirs.DownloadDir + '/' + fileName,
          // description: 'Downloading file...',
          description: '',
        },
      })
        .fetch('GET', url)
        .then(res => {
          // console.log('File saved to:', res.path());
        })
        .catch(err => {
          console.error('Download failed', err);
        });
    } else {
      // Web
      try {
        const response = await fetch(url);
        const blob = await response.blob();

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } catch (err) {
        console.error('Download failed', err);
      }
    }
  };

  const renderProfileImg = msg => {
    if (Platform.OS === 'web') {
      return (
        <View>
          {props.practitionerPhotoUrl ? (
            <View>
              <Image
                source={{
                  uri: props.practitionerPhotoUrl,
                }}
                resizeMode="cover"
                style={{
                  width: width(2),
                  height: width(2),
                  borderRadius: totalSize(10),
                }}
              />
            </View>
          ) : (
            <View style={styles.smallAvatarContainer}>
              <Text style={styles.smallAvatarText}>
                {props.doctorName
                  ? props.doctorName
                    .split(' ')
                    .filter(Boolean) // remove empty strings
                    .reduce((acc, word, i, arr) => {
                      if (i === 0 || i === arr.length - 1) {
                        acc += word[0];
                      }
                      return acc;
                    }, '')
                    .toUpperCase()
                  : 'DR'}
              </Text>
            </View>
          )}
        </View>
      );
    } else {
      return (
        <View
          style={{
            backgroundColor: colorPalette.secondary,
            borderWidth: 1,
            borderColor: colorPalette.secondary,
            borderRadius: totalSize(5),
            padding: totalSize(0.5),
          }}>
          <Text>AD</Text>
        </View>
      );
    }
  };

  useEffect(() => {
    if (attachment && attachment.image !== null) {
      uploadMutation.mutate({
        senderType: props.senderType,
        practitionerId: practitionerId,
        image: attachment.image,
      });

      setAttachment({ image: null });
    }
  }, [attachment]);

  // Track keyboard open/close state
  useEffect(() => {
    const keyboardWillShow = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      () => {
        setIsKeyboardOpen(true);
      }
    );

    const keyboardWillHide = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        setIsKeyboardOpen(false);
      }
    );

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, []);

  // Reset height to minimum when text is empty for smooth transition
  useEffect(() => {
    if (!watchedMessage || watchedMessage.trim().length === 0) {
      if (inputHeight !== MIN_H) {
        // Use LayoutAnimation for smooth height transition
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setInputHeight(MIN_H);
      }
      // Reset scrolling state when text is empty
      if (shouldScrollInTextarea) {
        setShouldScrollInTextarea(false);
      }
    }
  }, [watchedMessage, inputHeight, shouldScrollInTextarea]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  // Threshold for "at top" detection (more forgiving)
  const SCROLL_TOP_THRESHOLD = 10;

  // Set initial refresh state when dimensions are measured
  useEffect(() => {
    if (scrollViewHeight > 0 && contentHeight > 0) {
      const isScrollable = contentHeight > scrollViewHeight;
      if (!isScrollable) {
        setCanRefresh(true);
      } else {
        setCanRefresh(scrollY <= SCROLL_TOP_THRESHOLD);
      }
    } else {
      // Default to allowing refresh if dimensions not yet measured
      setCanRefresh(true);
    }
  }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          // Allow refresh when at or near the top (within threshold)
          setCanRefresh(scrollY <= SCROLL_TOP_THRESHOLD);
        }
      } else {
        // Default to allowing refresh if dimensions not yet measured
        setCanRefresh(true);
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );

  const handleScroll = (event: any) => {
    const y = event.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    // Update refresh state immediately on scroll
    if (!isScrollable) {
      setCanRefresh(true); // always allow if not scrollable
    } else {
      // Allow refresh when at or near the top (within threshold for smoother experience)
      setCanRefresh(y <= SCROLL_TOP_THRESHOLD);
    }

    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const isAtBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height - 50; // tolerance

    isAtBottomRef.current = isAtBottom;
    setShowScrollToBottom(!isAtBottom);
  };

  const handleScrollEndDrag = (event: any) => {
    // Ensure state is updated when user stops dragging
    const y = event.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;
    if (!isScrollable) {
      setCanRefresh(true);
    } else {
      setCanRefresh(y <= SCROLL_TOP_THRESHOLD);
    }
  };

  const handleOutsidePress = () => {
    Keyboard.dismiss();
    textInputRef.current?.blur();
  };

  const handleScrollBeginDrag = () => {
    Keyboard.dismiss();
    textInputRef.current?.blur();
  };

  return (
    <View style={styles.chatContainer}>
      {/* Messages */}
      {/* <TouchableWithoutFeedback onPress={handleOutsidePress}> */}
      <View style={styles.messagesContainer}>
        <ScrollView
          onScroll={handleScroll}
          onScrollBeginDrag={handleScrollBeginDrag}
          onScrollEndDrag={handleScrollEndDrag}
          scrollEventThrottle={16}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
          onLayout={e => {
            if (e?.nativeEvent?.layout?.height) {
              setScrollViewHeight(e.nativeEvent.layout.height);
            }
          }}
          onContentSizeChange={(contentWidth, newContentHeight) => {
            setContentHeight(newContentHeight);
            // Only follow new content down when the admin is already at the
            // bottom of the thread (e.g. actively chatting) - never yank
            // the view down while they're scrolled up reading history.
            if (isAtBottomRef.current) {
              const delay = Platform.OS === 'android' && isKeyboardOpen ? 100 : 0;
              setTimeout(() => {
                scrollViewRef.current?.scrollToEnd({ animated: true });
              }, delay);
            }
          }}
          ref={scrollViewRef}
          contentContainerStyle={{
            paddingBottom:
              Platform.OS === 'web'
                ? totalSize(1)
                : isKeyboardOpen
                  ? height(10)
                  : Platform.OS === 'android'
                    ? totalSize(12)
                    : totalSize(8),
            flexGrow: 1,
          }}
          style={{
            flex: 1,
            width: Platform.OS === 'web' ? '100%' : width(100),
            padding: Platform.OS === 'web' ? totalSize(0) : totalSize(1),
          }}>
          {isLoading && (
            <ActivityIndicator
              size={Platform.OS === 'web' ? totalSize(2) : totalSize(4)}
              color={colorPalette.secondary}
              style={{ marginTop: height(5) }}
            />
          )}

          {!isLoading &&
            messages.map((group: any, groupIndex: number) => (
              <View key={groupIndex}>
                {/* Date Separator */}
                <View
                  style={{
                    alignItems: 'center',
                    marginVertical: height(1),
                  }}>
                  <Text
                    style={{
                      fontSize:
                        Platform.OS === 'web' ? totalSize(1) : totalSize(1.6),
                      fontFamily: 'Quicksand-SemiBold',
                      color: colors.gray.default,
                    }}>
                    {moment.utc(group.date).calendar(null, {
                      sameDay: '[Today]',
                      lastDay: '[Yesterday]',
                      lastWeek: 'ddd, MMM D',
                      sameElse: 'MMM D, YYYY',
                    })}
                  </Text>
                </View>

                {/* Messages for that date */}
                {group.messages.map((msg: any, msgIndex: number) => {
                  const isPractitioner = msg.senderType === 'practitioner';
                  const isAttachment = msg.messageType === 'attachment';
                  const isImage =
                    msg.attachmentContentType &&
                    msg.attachmentContentType.startsWith('image/');

                  var isMessageRightPositioned = false;
                  if (props.user.userRole === 'admin') {
                    if (isPractitioner) {
                      isMessageRightPositioned = false;
                    } else {
                      isMessageRightPositioned = true;
                    }
                  } else {
                    if (isPractitioner) {
                      isMessageRightPositioned = true;
                    } else {
                      isMessageRightPositioned = false;
                    }
                  }

                  return (
                    <View
                      key={msgIndex}
                      style={{
                        alignSelf: !isMessageRightPositioned
                          ? 'flex-start'
                          : 'flex-end',
                        backgroundColor: 'transparent',
                        borderColor: colorPalette.primary,
                        borderWidth: totalSize(0.1),
                        padding: totalSize(0.5),
                        // paddingTop: totalSize(0.5),
                        // paddingBottom: totalSize(0.5),
                        borderRadius: totalSize(0.4),
                        marginVertical: height(0.5),
                        maxWidth: Platform.OS === 'web' ? width(35) : width(70),
                        minWidth: Platform.OS === 'web' ? width(0) : width(30),
                        flexDirection: 'row',
                        gap: Platform.OS === 'web' ? width(0.5) : width(2),
                      }}>
                      {/* If message */}
                      {msg.senderType !== props.user.userRole && (
                        <View>{renderProfileImg(msg)}</View>
                      )}
                      <View>
                        {!isAttachment && (
                          <Text
                            style={{
                              fontSize:
                                Platform.OS === 'web'
                                  ? totalSize(0.9)
                                  : totalSize(1.5),
                              fontFamily: 'Quicksand-Regular',
                              color: isPractitioner
                                ? colorPalette.black
                                : colorPalette.black,
                              flexWrap: 'wrap',
                              flexShrink: 1,
                              maxWidth:
                                msg.senderType !== props.user.userRole
                                  ? width(30)
                                  : width(100),
                            }}>
                            {msg.messageText}
                          </Text>
                        )}

                        {/* If attachment */}
                        {isAttachment && (
                          <View>
                            {isImage ? (
                              <>
                                <Image
                                  source={{ uri: msg.attachmentUrl }}
                                  style={{
                                    width:
                                      Platform.OS === 'web'
                                        ? width(25)
                                        : width(50),
                                    height:
                                      Platform.OS === 'web'
                                        ? height(15)
                                        : width(25),
                                    borderRadius: totalSize(0.5),
                                    marginBottom: height(0.5),
                                  }}
                                  resizeMode="cover"
                                />
                                <TouchableOpacity
                                  onPress={() =>
                                    downloadFile(
                                      msg.attachmentUrl,
                                      msg.attachmentFileName,
                                    )
                                  }>
                                  <Text
                                    style={{
                                      color: colorPalette.primary,
                                      fontSize:
                                        Platform.OS === 'web'
                                          ? totalSize(0.8)
                                          : totalSize(1.5),
                                      textDecorationLine: 'underline',
                                      fontFamily: 'Quicksand-SemiBold',
                                    }}>
                                    Download Image
                                  </Text>
                                </TouchableOpacity>
                              </>
                            ) : (
                              <>
                                <Text
                                  style={{
                                    color: colorPalette.black,
                                    fontSize:
                                      Platform.OS === 'web'
                                        ? totalSize(0.9)
                                        : totalSize(1.5),
                                    marginBottom: height(0.5),
                                  }}>
                                  {msg.attachmentFileName}
                                </Text>
                                <TouchableOpacity
                                  onPress={() =>
                                    downloadFile(
                                      msg.attachmentUrl,
                                      msg.attachmentFileName,
                                    )
                                  }>
                                  <Text
                                    style={{
                                      color: colorPalette.primary,
                                      fontSize:
                                        Platform.OS === 'web'
                                          ? totalSize(0.8)
                                          : totalSize(1.5),
                                      textDecorationLine: 'underline',
                                      fontFamily: 'Quicksand-SemiBold',
                                    }}>
                                    Download File
                                  </Text>
                                </TouchableOpacity>
                              </>
                            )}
                          </View>
                        )}

                        {/* Timestamp */}
                        <View
                          style={{
                            flex: 1,
                            alignContent: 'flex-end',
                            alignItems: 'flex-end',
                            maxWidth:
                              Platform.OS === 'web' ? width(35) : width(70),
                            minWidth:
                              Platform.OS === 'web' ? width(0) : width(30),
                          }}>
                          <Text
                            style={{
                              fontSize:
                                Platform.OS === 'web'
                                  ? totalSize(0.65)
                                  : totalSize(1.25),
                              color: colors.gray.darker,
                              marginTop: height(1.5),
                              textAlign: 'right',
                              fontFamily: 'Quicksand-Medium',
                            }}>
                            {moment
                              .utc(msg.createdAt)
                              .local()
                              .format('hh:mm A')}
                          </Text>
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>
            ))}
        </ScrollView>
      </View>
      {/* </TouchableWithoutFeedback> */}
      {showScrollToBottom && (
        <TouchableOpacity
          onPress={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: inputHeight + height(15),
            right: Platform.OS === 'web' ? width(1) : width(2),
            backgroundColor: colorPalette.secondary,
            borderRadius: totalSize(5),
            padding: totalSize(0.5),
            borderColor: colorPalette.secondary,
            borderWidth: 1,
            shadowColor: colorPalette.grey,
            shadowOpacity: 0.2,
            shadowRadius: 4,
            elevation: 5,
          }}>
          <Icon
            type="material-community"
            name="arrow-down"
            color={colorPalette.white}
            size={Platform.OS === 'web' ? totalSize(1.5) : totalSize(3)}
          />
        </TouchableOpacity>
      )}

      {/* Footer */}
      <View
        style={{
          width: Platform.OS === 'web' ? '100%' : width(100),
          position: Platform.OS === 'web' ? 'relative' : 'absolute',
          bottom: Platform.OS === 'web' ? undefined : height(0),
          flexShrink: 0,
        }}>
        {/* {isAttachmentUploadLoader && (
          <View style={{ width: width(100), alignItems: 'center', justifyContent: 'center' }}>
            <ActivityIndicator
              size={Platform.OS === 'web' ? totalSize(1.5) : totalSize(3.5)}
              color={colorPalette.secondary}
              style={{
                marginBottom: Platform.OS === 'web' ? height(1) : height(0),
              }}
            />
          </View>
        )} */}

        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: PAD_H,
            right: PAD_H + actionsW,
            top: PAD_V,
            opacity: 0,
            zIndex: -1,
          }}>
          <Text
            key={`${watchedMessage.length}-${watchedMessage.slice(0, 10)}-${watchedMessage.slice(-10)}`} // Force re-measurement when text changes (using length + first/last chars for performance)
            onLayout={e => {
              // Add null checks to prevent errors and capture values immediately
              if (!e || !e.nativeEvent || !e.nativeEvent.layout) {
                return;
              }

              // Capture the height value immediately before async callback
              const measuredHeight = e.nativeEvent.layout.height;

              // Validate measured height
              if (!measuredHeight || isNaN(measuredHeight) || measuredHeight <= 0) {
                return;
              }

              // Use requestAnimationFrame for smoother updates
              requestAnimationFrame(() => {
                // Calculate total height: measured text height + vertical padding (top + bottom)
                // The TextInput has paddingVertical: PAD_V, so we add PAD_V * 2
                const totalHeight = measuredHeight + (PAD_V * 2);
                const clamped = Math.max(MIN_H, Math.min(totalHeight, MAX_H));

                // Determine if scrolling should be enabled (when content exceeds MAX_H)
                const needsScrolling = totalHeight > MAX_H;

                // Update scrolling state if it changed
                if (needsScrolling !== shouldScrollInTextarea) {
                  setShouldScrollInTextarea(needsScrolling);
                }

                // Update height smoothly - use a threshold to prevent micro-adjustments
                if (Math.abs(clamped - inputHeight) >= 0.5) {
                  // Use LayoutAnimation for smooth height transitions
                  LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                  setInputHeight(clamped);
                }
              });
            }}
            style={{
              fontSize: inputFontSize,
              lineHeight: inputLineHeight,
              padding: INPUT_PAD,
              // Width is automatically constrained by parent's left/right positioning
              // This ensures the measurement Text has the same available width as the TextInput
              // Ensure text wrapping matches TextInput behavior
              flexWrap: 'wrap',
            }}>
            {measuredText}
          </Text>
        </View>

        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: totalSize(1),
          padding: totalSize(1),
          backgroundColor: colorPalette.white,
        }}>
          <View>
            {  !isInputFocused && !watchedMessage.trim() && (
              <TouchableOpacity
                onPress={handlePickFile}
                disabled={isAttachmentUploadLoader}
                style={{
                  paddingHorizontal:
                    Platform.OS === 'web' ? width(1.5) : width(0),
                  opacity: isAttachmentUploadLoader ? 0.5 : 1,
                }}>
                {/* <Image
                  source={PlusIcon}
                  style={{
                    width:
                      Platform.OS === 'web' ? totalSize(2) : totalSize(2.8),
                    height:
                      Platform.OS === 'web' ? totalSize(2) : totalSize(2.8),
                    resizeMode: 'contain',
                  }}
                /> */}
                <SVGIcon name="camera-29" size={totalSize(2.5)} />

              </TouchableOpacity>
            )}
          </View>

          <View
            ref={textInputContainerRef}
            style={{
              flexDirection: 'row',
              alignItems: 'flex-end',
              borderWidth: 1,
              borderColor: colorPalette.grey,
              borderRadius: totalSize(8),
              paddingHorizontal: PAD_H,
              paddingVertical: PAD_V,
              backgroundColor: colorPalette.white,
              marginBottom: Platform.OS === 'web' ? 10 : 0,
              position: 'relative',
              flex: 1,
            }}>

            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  ref={textInputRef}
                  placeholder="Type a message"
                  placeholderTextColor={colors.gray.light}
                  multiline
                  scrollEnabled={shouldScrollInTextarea}
                  value={value}
                  editable={!isAttachmentUploadLoader}
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => {
                    setIsInputFocused(false);
                    onBlur();
                  }}
                  onChangeText={messageTxt => {
                    onChange(messageTxt);
                    // Scroll to bottom when typing
                    setTimeout(
                      () =>
                        scrollViewRef.current?.scrollToEnd({
                          animated: true,
                        }),
                      100,
                    );
                  }}
                  style={{
                    flex: 1,
                    height: inputHeight,
                    fontSize: inputFontSize,
                    lineHeight: inputLineHeight,
                    color: colorPalette.black,
                    padding: INPUT_PAD,
                    textAlignVertical: 'top',
                    outline: 'none',
                    overflow: shouldScrollInTextarea ? 'scroll' : 'hidden', // Enable scrolling when content exceeds max height
                    opacity: isAttachmentUploadLoader ? 0.5 : 1,
                  }}
                  underlineColorAndroid="transparent"
                />
              )}
              name="messageText"
            />

          </View>

          <View>
            {isAttachmentUploadLoader && (

              <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                <ActivityIndicator
                  size={Platform.OS === 'web' ? totalSize(1.5) : totalSize(3.5)}
                  color={colorPalette.secondary}
                  style={{
                    marginBottom: Platform.OS === 'web' ? height(1) : height(0),
                  }}
                />
              </View>
            )}

            {!isAttachmentUploadLoader && (
              <TouchableOpacity
                onPress={handleSendPress}
                style={{
                  backgroundColor: colorPalette.secondary,
                  padding: totalSize(1.5),
                  borderRadius: totalSize(10),
                  width: totalSize(5.5),
                  height: totalSize(5.5),
                  alignContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'center',
                  paddingHorizontal:
                    Platform.OS === 'web' ? width(0) : width(1),
                }}>
                {/* <Image
                    source={SendIcon}
                    style={{
                      width:
                        Platform.OS === 'web' ? totalSize(2) : totalSize(2.8),
                      height:
                        Platform.OS === 'web' ? totalSize(2) : totalSize(2.8),
                      resizeMode: 'contain',
                    }}
                  /> */}
                <SVGIcon
                  name="send-white"
                  size={Platform.OS === 'web' ? totalSize(2) : totalSize(2.8)}
                />
              </TouchableOpacity>
            )}

          </View>
        </View>

        {/* <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-end',
            borderWidth: 1,
            borderColor: colorPalette.grey,
            borderRadius: 10,
            paddingHorizontal: PAD_H,
            paddingVertical: PAD_V,
            backgroundColor: colorPalette.white,
            margin: 10,
            marginBottom: Platform.OS === 'web' ? 10 : 0,
            position: 'relative',
          }}>
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: PAD_H,
              right: PAD_H + actionsW,
              top: PAD_V,
              opacity: 0,
              zIndex: -1,
            }}>
            <Text
              onLayout={e => {
                if (!e?.nativeEvent?.layout?.height) return;
                const h = e.nativeEvent.layout.height + PAD_V * 2;
                const clamped = Math.max(MIN_H, Math.min(h, MAX_H));
                if (clamped !== inputHeight) setInputHeight(clamped);
              }}
              style={{
                fontSize: 16,
                lineHeight: 20,
                padding: INPUT_PAD,
              }}>
              {measuredText}
            </Text>
          </View>

          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                placeholder="Type a message"
                placeholderTextColor={colors.gray.light}
                multiline
                scrollEnabled={false}
                value={value}
                onChangeText={messageTxt => {
                  onChange(messageTxt);
                  setTimeout(
                    () =>
                      scrollViewRef.current?.scrollToEnd({
                        animated: true,
                      }),
                    100,
                  );
                }}
                style={{
                  flex: 1,
                  height: inputHeight,
                  fontSize:
                    Platform.OS === 'web' ? totalSize(1) : totalSize(1.6),
                  color: colorPalette.black,
                  padding: INPUT_PAD,
                  textAlignVertical: 'top',
                  outline: 'none',
                }}
                underlineColorAndroid="transparent"
              />
            )}
            name="messageText"
          />

          <View
            onLayout={e => {
              if (e?.nativeEvent?.layout?.width) {
                setActionsW(e.nativeEvent.layout.width);
              }
            }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginLeft: totalSize(2),
              alignSelf: 'center',
            }}>
            {isAttachmentUploadLoader && (
              <ActivityIndicator
                size={Platform.OS === 'web' ? totalSize(1.5) : totalSize(3.5)}
                color={colorPalette.secondary}
                style={{
                  marginBottom: Platform.OS === 'web' ? height(1) : height(0),
                }}
              />
            )}

            {!isAttachmentUploadLoader && (
              <TouchableOpacity
                onPress={handlePickFile}
                style={{
                  paddingHorizontal:
                    Platform.OS === 'web' ? width(1.5) : width(3.5),
                }}>
                <Image
                  source={PlusIcon}
                  style={{
                    width:
                      Platform.OS === 'web' ? totalSize(2) : totalSize(2.8),
                    height:
                      Platform.OS === 'web' ? totalSize(2) : totalSize(2.8),
                    resizeMode: 'contain',
                  }}
                />
               
              </TouchableOpacity>
            )}


          </View>
        </View> */}
      </View>

      {Platform.OS !== 'web' && (
        <ActionSheet
          ref={actionSheetRef}
          title={'Choose an option'}
          options={[
            'Cancel',
            'Take Photo',
            'Select from Gallery',
            'Select File',
          ]}
          cancelButtonIndex={0}
          onPress={handleOption}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  chatContainer: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  messagesContainer: {
    flex: 1,
    minHeight: 0,
  },
  avatarContainer: {
    padding: height(9),
    backgroundColor: colors.gray.lighter,
    borderRadius: totalSize(20),
    justifyContent: 'center',
    alignItems: 'center',
  },
  smallAvatarContainer: {
    padding: height(2),
    backgroundColor: colors.gray.lighter,
    borderRadius: totalSize(6),
    justifyContent: 'center',
    alignItems: 'center',
  },
  smallAvatarText: {
    fontSize: totalSize(1),
    fontFamily: 'Quicksand-Bold',
  },
  avatarText: {
    fontSize: totalSize(4),
    fontFamily: 'Quicksand-Bold',
  },
});

const doctorDetailStyles = StyleSheet.create({
  heading: {
    fontFamily: 'Quicksand-SemiBold',
    fontSize: totalSize(1),
  },
  doctorNameTxt: {
    fontFamily: 'Quicksand-Regular',
    fontSize: totalSize(1),
  },
  doctorCasesTxt: {
    fontFamily: 'Quicksand-Regular',
    fontSize: totalSize(1),
    marginTop: height(2),
    marginLeft: height(1.5),
  },
});

export default memo(AdminDoctorChat);
